/* Missing Bills & Sales Reconciliation — upload the daily Invoice-Wise sales file, preview how
   the portal differs (missing / different / extra), then apply so the portal matches the file. */
import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "./_shared";
import { UploadCloud, RefreshCw, Mail } from "lucide-react";
import { RecoAnalysis } from "./_reco_analysis";

const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function MissingBills() {
  const [list, setList] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([api.get("/reco/invoice/analyses"), api.get("/reco/status")]);
      setList(a.data.analyses || []);
      setCfg(s.data.config);
      setSel((cur) => cur || a.data.analyses?.[0]?.id || null);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!list.some((a) => ["analysing", "applying"].includes(a.status))) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [list, load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/reco/invoice/analyse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180_000 });
      toast.success("File received — comparing with the portal…");
      setSel(r.data.id);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="missing-bills-page">
      <PageHeader title="Missing Bills & Sales Reconciliation" subtitle="DATA · DAILY INVOICE-WISE FILE"
        actions={<button onClick={load} className="k-btn k-btn-outline k-btn-sm" data-testid="missing-bills-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>} />
      <div className="p-8 space-y-6 max-w-6xl">
        <div className="chart-card p-5 space-y-3" data-accent="burgundy" data-testid="missing-bills-upload-card">
          <p className="text-sm text-neutral-600">
            Upload the <b>Invoice-Wise</b> sales file (.xlsx or .csv). The portal is compared bill-by-bill: missing bills are added,
            bills with a different amount, store, date or return status are corrected to match the file, and portal bills
            not in the file are flagged for review. <b>Nothing changes until you press Apply.</b>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={inputRef} type="file" accept=".xlsx,.csv" data-testid="missing-bills-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-neutral-600 file:mr-3 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-neutral-900 file:text-white file:cursor-pointer" />
            <button onClick={upload} disabled={busy || !file} className="k-btn kazo-bg-burgundy text-white" data-testid="missing-bills-upload-btn">
              <UploadCloud className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload & compare"}
            </button>
          </div>
          <div className="text-xs text-neutral-500 flex items-center gap-1.5" data-testid="missing-bills-email-status">
            <Mail className="w-3.5 h-3.5" />
            {cfg?.configured
              ? `Email auto-pickup: ${cfg.daily_enabled ? `on, daily ${cfg.daily_time_ist} IST` : "connected, schedule off"} (${cfg.user})`
              : "Email auto-pickup: waiting for the Microsoft 365 app connection"}
          </div>
        </div>

        {list.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="reco-analysis-list">
            {list.map((a) => (
              <button key={a.id} onClick={() => setSel(a.id)} data-testid={`reco-analysis-${a.id}`}
                className={`k-btn k-btn-sm ${sel === a.id ? "kazo-bg-burgundy text-white" : "k-btn-outline"}`}>
                {when(a.created_at)} · {a.status}
              </button>
            ))}
          </div>
        )}
        {sel && <RecoAnalysis key={sel} id={sel} onChanged={load} />}
      </div>
    </div>
  );
}
